import {
  Button,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { MODAL_FOOTER_PB, MODAL_HEADER_PT } from '@/constants/mobileLayout';
import { isScoreReassignmentNoticeVisible } from '@/constants/scoreReassignmentNotice';
import { useMobileFormModal } from '@/hooks/useMobileFormModal';

function ScoreReassignmentNoticeModal(props: { isOpen: boolean; onClose: () => void }) {
  const { isOpen, onClose } = props;
  const { modalSize, isCentered, scrollBehavior, contentProps, footerPb } = useMobileFormModal();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered={isCentered}
      size={modalSize}
      scrollBehavior={scrollBehavior}
    >
      <ModalOverlay />
      <ModalContent {...contentProps}>
        <ModalHeader pt={MODAL_HEADER_PT} fontSize={{ base: 'lg', md: 'xl' }}>
          Why did my score change?
        </ModalHeader>
        <ModalCloseButton mt={2} mr={2} />
        <ModalBody pb={footerPb}>
          <VStack align='stretch' spacing={4}>
            <Text fontSize='sm'>
              If your points or league rank look different from what you expected, that is expected after
              we corrected how matches are grouped into Matchdays.
            </Text>
            <Text fontSize='sm' fontWeight='semibold'>
              The 4:30am London cutoff
            </Text>
            <Text fontSize='sm'>
              Each Matchday now runs on a fixed window in UK time. The cut-off between one Matchday and
              the next is <strong>4:30am</strong> (British Summer Time):
            </Text>
            <List spacing={2} styleType='disc' pl={4} fontSize='sm'>
              <ListItem>
                Kick-offs <strong>before 4:30am</strong> count as <strong>night games</strong> for the{' '}
                <strong>previous</strong> calendar day&apos;s Matchday.
              </ListItem>
              <ListItem>
                Kick-offs at <strong>4:30am or later</strong> count as <strong>morning games</strong> for
                that calendar day&apos;s Matchday.
              </ListItem>
            </List>
            <Text fontSize='sm'>
              Your total score is still the sum of your teams&apos; weighted points across every result
              that counts for you — only <em>which Matchday</em> some games fall into may have shifted.
            </Text>
            <Text fontSize='sm' fontWeight='semibold'>
              When leagues update
            </Text>
            <Text fontSize='sm'>
              After the last game in a Matchday finishes, points and league standings are recalculated.
              That happens automatically, and your leagues will always show the latest settled totals by{' '}
              <strong>6:00am British Summer Time at the latest</strong> for that Matchday window.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter pb={footerPb}>
          <Button colorScheme='red' variant='outline' onClick={onClose}>
            Got it
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function ScoreReassignmentNoticeButton(props: { size?: 'xs' | 'sm' }) {
  const { size = 'sm' } = props;
  const disclosure = useDisclosure();
  const visible = isScoreReassignmentNoticeVisible();

  if (!visible) {
    return null;
  }

  return (
    <>
      <Button
        size={size}
        colorScheme='red'
        variant='outline'
        fontSize='xs'
        fontWeight='semibold'
        lineHeight='short'
        whiteSpace='normal'
        textAlign='center'
        h='auto'
        minH={7}
        py={1}
        px={2}
        onClick={disclosure.onOpen}
      >
        What happened to my score?
      </Button>
      <ScoreReassignmentNoticeModal isOpen={disclosure.isOpen} onClose={disclosure.onClose} />
    </>
  );
}
